import { useState, useEffect, useRef, useCallback } from "react";
import { Client } from "@stomp/stompjs";
import SockJS from "sockjs-client";
import { useAuth } from "../Context/AuthProvider";
import { jwtDecode } from "jwt-decode";
import { useMAInventorySubscriptions } from "./useMAInventorySubscriptions";
import { insertFrontendLog } from "../http/api";
import {
  MA_MD_DEBUG_PREFIX,
  normalizeCusipList,
  describeCusipCollection,
} from "./maInventoryMarketDataUtils";
import { getWatchlistStatusUpdatePayload } from "./maWatchlistStatusUtils";

// ---------------------------------------------------------------------------
// Summary of WebSocket/STOMP subscription behaviour (PAGE-WISE LAZY MODEL):
//
// Subscriptions are grouped by page. Each group is subscribed AT MOST ONCE
// per session (on first page visit). Navigation away does NOT unsubscribe.
// Only logout / disconnect resets state.
//
// Groups:
//   'global'            — always subscribed on connect:
//       /topic/db-subscription-ack/{userId}
//       /user/{userId}/bond-calc
//
//   'counterParties'    — subscribed when /counter-parties is first visited:
//       /topic/counter-party
//
//   'sessionUserConfig' — subscribed when /session-user-management is first visited:
//       /topic/fix-user-config
//       /topic/fix-session-config
//
//   'accountPositions'  — subscribed when /account-positions or /portfolio-rebalance* is first visited:
//       /user/{userId}/account-mapping/account-update
//       /user/{accountId}/account-mapping/accountSummary     (requires selectedAccountId)
//       /user/{accountId}/account-mapping/position-update    (requires selectedAccountId)
//
//   'orders'            — subscribed when /order or OrderEntry is first opened:
//       sharedBlotter === true  → /topic/customer/{custId}/(order|characteristic|order-quotes|ladder)
//       sharedBlotter === false → /user/{userId}/(order|characteristic|order-quotes|ladder)
//
//   'maInventory'       — dynamic per-CUSIP, managed separately by useMAInventorySubscriptions:
//       /topic/md-update/{CUSIP}  (DO NOT TOUCH)
//
//   'maInventoryPage'   — page-scoped topic while MA Inventory page is mounted:
//       /topic/ma-inventory-page
//
//   'secMaster'         — subscribed when /sec-master or /watchlist (MA Inventory) is visited:
//       /topic/sec-master-security-update  → { secMaster: <full row>, lastUpdatedAt }; dispatches
//                                            'secMasterSecurityUpdate' CustomEvent; SecMaster merges the
//                                            row into displayed rows and refreshes its "Updated at",
//                                            MA Inventory patches the matching identifier in store.
//       /topic/sec-master-filter-refresh   → {"action":"refresh"}; dispatches 'secMasterFilterRefresh'
//                                            CustomEvent; SecMaster re-fetches all dropdown option APIs,
//                                            MA Inventory re-fetches watchlist shared filter options.
// ---------------------------------------------------------------------------

let isInstanceMounted = false;

const getBondCalcLocalTimestamp = () => {
  const now = new Date();
  const pad = (value, length = 2) => String(value).padStart(length, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}.${pad(now.getMilliseconds(), 3)}`;
};

const logBondCalcDebug = (stage, payload) => {
  console.log("debugging-bond-calc", `[${getBondCalcLocalTimestamp()}] ${stage}`, payload);
};

const logMAInventoryProbe = (stage, payload) => {
  console.log("[MA-MD-PROBE]", stage, payload);
};

const buildDbSubscriptionAckPath = (userId) => `/topic/db-subscription-ack/${userId}`;


const WebSocketComponent = ({
  onConnectionStateChange,
  desiredMAInventoryCusips = [],
  maInventoryRequestTraceId = "",
  pageSubscriptionGroups = new Set(),
}) => {
  const stompClientRef = useRef(null);
  const { userId, role: userRole, token: jwtToken, isLoggedIn } = useAuth();
  const subscriptionsRef = useRef([]);
  const [connectionState, setConnectionState] = useState("disconnected");
  const mountedRef = useRef(false);
  const activeSubscriptionsPathsRef = useRef(new Set());
  const [instanceId] = useState(() => Math.random().toString(36).substring(2, 10));
  const [currentAccountId, setCurrentAccountId] = useState(null);
  const accountUpdateSubscriptionRef = useRef(null);
  const processedDbAckFailureTraceIdsRef = useRef(new Set());
  const maInventoryTraceIdRef = useRef("");

  // Tracks which subscription groups have already been subscribed on the STOMP client.
  // This is the runtime guard — separate from pageSubscriptionGroups (provider-level intent).
  const subscribedGroupsRef = useRef(new Set());
  const groupSubscriptionsRef = useRef(new Map());

  // Hold decoded token data that is needed by subscribeGroup
  const decodedTokenRef = useRef(null);

  const { clearMAInventorySubscriptions } = useMAInventorySubscriptions({
    client: stompClientRef.current,
    connectionState,
    desiredCusips: desiredMAInventoryCusips,
    instanceId,
  });

  useEffect(() => {
    maInventoryTraceIdRef.current = String(maInventoryRequestTraceId || "").trim();
  }, [maInventoryRequestTraceId]);

  useEffect(() => {
    if (!maInventoryRequestTraceId) {
      return;
    }

    processedDbAckFailureTraceIdsRef.current.delete(String(maInventoryRequestTraceId).trim());
  }, [maInventoryRequestTraceId]);

  const handleDbSubscriptionAck = useCallback((rawAckPayload) => {
    const ack = rawAckPayload && typeof rawAckPayload === "object" ? rawAckPayload : {};
    const ackTraceId = String(ack.traceId || "").trim();
    const activeTraceId = String(maInventoryTraceIdRef.current || "").trim();
    const status = String(ack.status || "").trim().toLowerCase();
    const watchlistId = ack.watchlistId;
    const subscribeCusips = normalizeCusipList(Array.isArray(ack.subscribeCusips) ? ack.subscribeCusips : []);
    const unsubscribeCusips = normalizeCusipList(Array.isArray(ack.unsubscribeCusips) ? ack.unsubscribeCusips : []);

    console.log(
      `${MA_MD_DEBUG_PREFIX} DB_ACK_RECEIVED status=${status || "<empty>"} traceId=${ackTraceId || "<empty>"} activeTraceId=${activeTraceId || "<empty>"} watchlistId=${watchlistId ?? "<none>"}`,
      ack
    );

    window.dispatchEvent(
      new CustomEvent("maInventoryDbSubscriptionAck", {
        detail: ack,
      })
    );

    if (!ackTraceId) {
      console.log(`${MA_MD_DEBUG_PREFIX} DB_ACK_SKIPPED reason=missing-trace-id`);
      return;
    }

    if (!activeTraceId) {
      console.log(`${MA_MD_DEBUG_PREFIX} DB_ACK_SKIPPED traceId=${ackTraceId} reason=no-active-trace-id`);
      return;
    }

    if (ackTraceId !== activeTraceId) {
      console.log(`${MA_MD_DEBUG_PREFIX} DB_ACK_SKIPPED traceId=${ackTraceId} reason=trace-mismatch activeTraceId=${activeTraceId}`);
      return;
    }

    if (status === "success") {
      console.log(
        `${MA_MD_DEBUG_PREFIX} DB_ACK_SUCCESS traceId=${ackTraceId} watchlistId=${watchlistId ?? "<none>"} subscribeCusips=${describeCusipCollection(subscribeCusips)} unsubscribeCusips=${describeCusipCollection(unsubscribeCusips)}`
      );
      insertFrontendLog({
        socketEvent: "maInventoryDbSubscriptionAckSuccess",
        traceId: ackTraceId,
        watchlistId,
        subscribeCusips,
        unsubscribeCusips,
      });
      return;
    }

    if (status !== "failure") {
      console.warn(`${MA_MD_DEBUG_PREFIX} DB_ACK_SKIPPED traceId=${ackTraceId} reason=unsupported-status status=${status || "<empty>"}`);
      return;
    }

    if (processedDbAckFailureTraceIdsRef.current.has(ackTraceId)) {
      console.log(`${MA_MD_DEBUG_PREFIX} DB_ACK_FAILURE_SKIPPED traceId=${ackTraceId} reason=already-processed`);
      return;
    }

    processedDbAckFailureTraceIdsRef.current.add(ackTraceId);

    const errorMessage = String(ack.errorMessage || "").trim();
    const fallbackMessage = "Something went wrong for CUSIP subscriptions. Retrying...";
    const snackbarMessage = errorMessage
      ? `Something went wrong for CUSIP subscriptions. Retrying... (${errorMessage})`
      : fallbackMessage;

    console.error(
      `${MA_MD_DEBUG_PREFIX} DB_ACK_FAILURE traceId=${ackTraceId} watchlistId=${watchlistId ?? "<none>"} error=${errorMessage || "<none>"} subscribeCusips=${describeCusipCollection(subscribeCusips)} unsubscribeCusips=${describeCusipCollection(unsubscribeCusips)}`
    );

    insertFrontendLog({
      socketEvent: "maInventoryDbSubscriptionAckFailure",
      traceId: ackTraceId,
      watchlistId,
      subscribeCusips,
      unsubscribeCusips,
      errorMessage,
    });

    window.dispatchEvent(
      new CustomEvent("maInventoryDbSubscriptionFailure", {
        detail: {
          message: snackbarMessage,
          ack,
        },
      })
    );
  }, []);

  const emitOrderQuotesUpdate = (payload) => {
    if (!mountedRef.current || payload == null) return;

    const rawQuotes = Array.isArray(payload) ? payload : [payload];
    const filteredQuotes = rawQuotes.filter(Boolean);
    if (!filteredQuotes.length) return;

    const firstOrderId = filteredQuotes[0]?.orderId;
    const singleOrder =
      firstOrderId && filteredQuotes.every((quote) => quote?.orderId === firstOrderId);
    const sanitizedQuotes = filteredQuotes.map((quote) => {
      if (!singleOrder || quote?.orderId) return { ...quote };
      return { ...quote, orderId: firstOrderId };
    });

    const detail = singleOrder
      ? { orderId: firstOrderId, quotes: sanitizedQuotes }
      : { quotes: sanitizedQuotes };

    window.dispatchEvent(new CustomEvent("orderQuotesUpdate", { detail }));
  };

  // New: connection lifecycle helpers
  const connectInProgressRef = useRef(false);
  const healthCheckTimerRef = useRef(null);
  const lastConnectAttemptRef = useRef(0);
  const lastConnectedAtRef = useRef(0);

  const updateConnectionState = (newState) => {
    setConnectionState(newState);
    if (onConnectionStateChange) {
      onConnectionStateChange(newState);
    }
    window.dispatchEvent(
      new CustomEvent("websocketConnectionStateChange", { detail: newState })
    );
  };

  // ---------------------------------------------------------------------------
  // unsubscribeGroup — unsubscribe all topics for a named group.
  // ---------------------------------------------------------------------------
  const unsubscribeGroup = useCallback((groupName) => {
    if (!groupName) return;
    console.log(`[${instanceId}] unsubscribeGroup: group=${groupName}`);
    
    // 1. Unsubscribe STOMP subscriptions
    const subs = groupSubscriptionsRef.current.get(groupName);
    if (subs) {
      subs.forEach((subObj) => {
        console.log(`[${instanceId}] Unsubscribing from STOMP topic: ${subObj.path}`);
        subObj.sub.unsubscribe();
        activeSubscriptionsPathsRef.current.delete(subObj.path);
        const index = subscriptionsRef.current.indexOf(subObj.sub);
        if (index > -1) {
          subscriptionsRef.current.splice(index, 1);
        }
      });
      groupSubscriptionsRef.current.delete(groupName);
    }
    
    // 2. Special handling for accountPositions
    if (groupName === "accountPositions") {
      unsubscribeAccountSpecific();
    }
    
    subscribedGroupsRef.current.delete(groupName);
  }, [instanceId]);

  // ---------------------------------------------------------------------------
  // safeSubscribe — deduplication wrapper
  // ---------------------------------------------------------------------------
  const safeSubscribe = (client, path, groupName, callback) => {
    if (activeSubscriptionsPathsRef.current.has(path)) {
      console.log(`[${instanceId}] Already subscribed to ${path}, skipping duplicate subscription`);
      return null;
    }
    console.log(`[${instanceId}] Subscribing to: ${path}`);
    const wrappedCallback = (message) => {
      console.log(`[${instanceId}] Received message on ${path}`);
      callback(message);
    };
    const subscription = client.subscribe(path, wrappedCallback);
    activeSubscriptionsPathsRef.current.add(path);
    
    if (groupName) {
      if (!groupSubscriptionsRef.current.has(groupName)) {
        groupSubscriptionsRef.current.set(groupName, []);
      }
      groupSubscriptionsRef.current.get(groupName).push({ path, sub: subscription });
    }
    
    return subscription;
  };

  // ---------------------------------------------------------------------------
  // subscribeGroup — subscribe all topics for a named group, exactly once.
  // Guards against duplicates via subscribedGroupsRef.
  // ---------------------------------------------------------------------------
  const subscribeGroup = useCallback((client, groupName) => {
    if (!client || !client.connected) {
      console.log(`[${instanceId}] subscribeGroup: client not connected — deferring group=${groupName}`);
      return;
    }
    if (subscribedGroupsRef.current.has(groupName)) {
      console.log(`[${instanceId}] subscribeGroup: already subscribed group=${groupName} — skipping`);
      return;
    }

    const decoded = decodedTokenRef.current;
    const tokenCustId = decoded?.custId || decoded?.customerId;
    const sharedBlotter =
      decoded?.sharedBlotter === 1 ||
      decoded?.sharedBlotter === "1" ||
      decoded?.sharedBlotter === true;

    console.log(`[${instanceId}] subscribeGroup: subscribing group=${groupName}`);

    // ------------------------------------------------------------------
    if (groupName === "global") {
      // /topic/db-subscription-ack/{userId}
      const normalizedUserId = String(userId || "").trim();
      if (normalizedUserId) {
        const dbSubscriptionAckPath = buildDbSubscriptionAckPath(normalizedUserId);
        const dbAckSub = safeSubscribe(client, dbSubscriptionAckPath, groupName, (message) => {
          logMAInventoryProbe("DB_ACK_RAW", {
            path: dbSubscriptionAckPath,
            body: message?.body,
            traceId: maInventoryRequestTraceId || "<empty>",
          });

          let dbAckPayload = null;
          try {
            dbAckPayload = JSON.parse(message?.body);
          } catch (error) {
            console.error(`${MA_MD_DEBUG_PREFIX} DB_ACK_PARSE_ERROR path=${dbSubscriptionAckPath}`, error, message?.body);
            return;
          }

          console.log(`${MA_MD_DEBUG_PREFIX} DB_ACK_MESSAGE_RECEIVED path=${dbSubscriptionAckPath}`, dbAckPayload);
          handleDbSubscriptionAck(dbAckPayload);
        });
        if (dbAckSub) subscriptionsRef.current.push(dbAckSub);
      } else {
        console.warn(`${MA_MD_DEBUG_PREFIX} DB_ACK_SUBSCRIBE_SKIPPED reason=missing-user-id`);
      }

      // /user/{userId}/bond-calc
      const userBondCalcPath = `/user/${userId}/bond-calc`;
      const userBondCalcSub = safeSubscribe(client, userBondCalcPath, groupName, (message) => {
        if (!mountedRef.current) return;
        const bondCalc = JSON.parse(message.body);
        console.log("debugging", userBondCalcPath, bondCalc);
        logBondCalcDebug("update-received /user/{userId}/bond-calc", {
          path: userBondCalcPath,
          UUID: bondCalc?.UUID,
          status: bondCalc?.status,
          cusip: bondCalc?.cusip ?? bondCalc?.Cusip,
        });
        if (bondCalc.status === "error") {
          window.dispatchEvent(new CustomEvent("bondCalcError", { detail: bondCalc }));
        } else {
          window.dispatchEvent(new CustomEvent("bondCalcUpdate", { detail: bondCalc }));
        }
      });
      if (userBondCalcSub) subscriptionsRef.current.push(userBondCalcSub);

      // /user/{userId}/rebalance — async rebalance result (ACK flow).
      // Delivered globally so the result is received on any page; the
      // PortfolioRebalanceJobWatcher consumes the "rebalanceResult" event.
      const userRebalancePath = `/user/${userId}/rebalance`;
      const userRebalanceSub = safeSubscribe(client, userRebalancePath, groupName, (message) => {
        if (!mountedRef.current) return;
        let payload = null;
        try {
          payload = JSON.parse(message.body);
        } catch (error) {
          console.error("[rebalance ws] Failed to parse message", error, message?.body);
          return;
        }
        console.log("debugging", userRebalancePath, payload);
        window.dispatchEvent(new CustomEvent("rebalanceResult", { detail: payload }));
      });
      if (userRebalanceSub) subscriptionsRef.current.push(userRebalanceSub);

    // ------------------------------------------------------------------
    } else if (groupName === "counterParties") {
      const counterPartyPath = "/topic/counter-party";
      const counterPartySub = safeSubscribe(client, counterPartyPath, groupName, (message) => {
        if (!mountedRef.current) return;
        const counterParty = JSON.parse(message.body);
        console.log("debugging", counterPartyPath, counterParty);
        window.dispatchEvent(new CustomEvent("counterPartyUpdate", { detail: counterParty }));
      });
      if (counterPartySub) {
        console.log(`[${instanceId}] Subscribed to counter-party updates`);
        subscriptionsRef.current.push(counterPartySub);
      }

    // ------------------------------------------------------------------
    } else if (groupName === "sessionUserConfig") {
      const fixUserConfigPath = "/topic/fix-user-config";
      const fixUserConfigSub = safeSubscribe(client, fixUserConfigPath, groupName, (message) => {
        if (!mountedRef.current) return;
        const userConfigUpdate = JSON.parse(message.body);
        console.log("debugging", fixUserConfigPath, userConfigUpdate);
        window.dispatchEvent(new CustomEvent("fixUserConfigUpdate", { detail: userConfigUpdate }));
      });
      if (fixUserConfigSub) {
        console.log(`[${instanceId}] Subscribed to fix user config updates`);
        subscriptionsRef.current.push(fixUserConfigSub);
      }

      const fixSessionConfigPath = "/topic/fix-session-config";
      const fixSessionConfigSub = safeSubscribe(client, fixSessionConfigPath, groupName, (message) => {
        if (!mountedRef.current) return;
        const sessionConfigUpdate = JSON.parse(message.body);
        console.log("debugging", fixSessionConfigPath, sessionConfigUpdate);
        window.dispatchEvent(new CustomEvent("fixSessionConfigUpdate", { detail: sessionConfigUpdate }));
      });
      if (fixSessionConfigSub) {
        console.log(`[${instanceId}] Subscribed to fix session config updates`);
        subscriptionsRef.current.push(fixSessionConfigSub);
      }

    // ------------------------------------------------------------------
    } else if (groupName === "accountPositions") {
      // account-update (user-scoped, no accountId needed)
      const accountUpdatePath = `/user/${userId}/account-mapping/account-update`;
      const accountUpdateSub = safeSubscribe(client, accountUpdatePath, groupName, (message) => {
        if (!mountedRef.current) return;
        const accountUpdate = JSON.parse(message.body);
        console.log("debugging", accountUpdatePath, accountUpdate);
        console.log("Account mapping update structure:", JSON.stringify(accountUpdate, null, 2));
        window.dispatchEvent(new CustomEvent("accountUpdate", { detail: accountUpdate }));
      });
      if (accountUpdateSub) {
        console.log(`[${instanceId}] Subscribed to account updates`);
        subscriptionsRef.current.push(accountUpdateSub);
      }

      // accountSummary & position-update are account-specific — subscribed via
      // subscribeAccountUpdate() when currentAccountId is set. Triggering that here
      // if an accountId is already known.
      const selectedAccount =
        currentAccountId ||
        window.selectedAccountId ||
        localStorage.getItem("selectedAccountId");
      if (selectedAccount) {
        subscribeAccountSpecific(client, selectedAccount);
      }

    // ------------------------------------------------------------------
    } else if (groupName === "orders") {
      if (sharedBlotter) {
        if (tokenCustId) {
          const customerOrderPath = `/topic/customer/${tokenCustId}/order`;
          const customerOrderSub = safeSubscribe(client, customerOrderPath, groupName, (message) => {
            if (!mountedRef.current) return;
            const order = JSON.parse(message.body);
            console.log("debugging", customerOrderPath, order);
            window.dispatchEvent(new CustomEvent("orderUpdate", { detail: order }));
          });
          if (customerOrderSub) subscriptionsRef.current.push(customerOrderSub);

          const customerCharPath = `/topic/customer/${tokenCustId}/characteristic`;
          const customerCharSub = safeSubscribe(client, customerCharPath, groupName, (message) => {
            if (!mountedRef.current) return;
            const characteristic = JSON.parse(message.body);
            console.log("debugging", customerCharPath, characteristic);
            window.dispatchEvent(new CustomEvent("characteristicUpdate", { detail: characteristic }));
          });
          if (customerCharSub) subscriptionsRef.current.push(customerCharSub);

          const customerQuotesPath = `/topic/customer/${tokenCustId}/order-quotes`;
          const customerQuotesSub = safeSubscribe(client, customerQuotesPath, groupName, (message) => {
            if (!mountedRef.current) return;
            console.log("debugging", customerQuotesPath, JSON.parse(message.body));
            emitOrderQuotesUpdate(JSON.parse(message.body));
          });
          if (customerQuotesSub) subscriptionsRef.current.push(customerQuotesSub);

          const customerLadderPath = `/topic/customer/${tokenCustId}/ladder`;
          const customerLadderSub = safeSubscribe(client, customerLadderPath, groupName, (message) => {
            if (!mountedRef.current) return;
            const ladder = JSON.parse(message.body);
            console.log("debugging", customerLadderPath, ladder);
            window.dispatchEvent(new CustomEvent("ladderUpdate", { detail: ladder }));
          });
          if (customerLadderSub) subscriptionsRef.current.push(customerLadderSub);

          console.log(`[${instanceId}] orders group — SHARED_BLOTTER mode subscribed`);
        } else {
          console.warn(`[${instanceId}] sharedBlotter enabled but no custId found in token; skipping customer order subscription`);
        }
      } else {
        const orderPath = `/user/${userId}/order`;
        const sub = safeSubscribe(client, orderPath, groupName, (message) => {
          if (!mountedRef.current) return;
          const order = JSON.parse(message.body);
          console.log("debugging", orderPath, order);
          window.dispatchEvent(new CustomEvent("orderUpdate", { detail: order }));
        });
        if (sub) subscriptionsRef.current.push(sub);

        const charPath = `/user/${userId}/characteristic`;
        const charSub = safeSubscribe(client, charPath, groupName, (message) => {
          if (!mountedRef.current) return;
          const characteristic = JSON.parse(message.body);
          console.log("debugging", charPath, characteristic);
          window.dispatchEvent(new CustomEvent("characteristicUpdate", { detail: characteristic }));
        });
        if (charSub) subscriptionsRef.current.push(charSub);

        const orderQuotesPath = `/user/${userId}/order-quotes`;
        const orderQuotesSub = safeSubscribe(client, orderQuotesPath, groupName, (message) => {
          if (!mountedRef.current) return;
          console.log("debugging", orderQuotesPath, JSON.parse(message.body));
          emitOrderQuotesUpdate(JSON.parse(message.body));
        });
        if (orderQuotesSub) subscriptionsRef.current.push(orderQuotesSub);

        const ladderPath = `/user/${userId}/ladder`;
        const ladderSub = safeSubscribe(client, ladderPath, groupName, (message) => {
          if (!mountedRef.current) return;
          const ladder = JSON.parse(message.body);
          console.log("debugging", ladderPath, ladder);
          window.dispatchEvent(new CustomEvent("ladderUpdate", { detail: ladder }));
        });
        if (ladderSub) subscriptionsRef.current.push(ladderSub);

        console.log(`[${instanceId}] orders group — USER_ONLY mode subscribed`);
      }
    } else if (groupName === "maInventoryPage") {
      const maInventoryPagePath = "/topic/ma-inventory-page";
      const maInventoryPageSub = safeSubscribe(client, maInventoryPagePath, groupName, (message) => {
        if (!mountedRef.current) return;

        let payload = null;
        try {
          payload = JSON.parse(message?.body);
        } catch (error) {
          console.warn(`${MA_MD_DEBUG_PREFIX} MA_INVENTORY_PAGE_PARSE_ERROR`, error, message?.body);
          return;
        }

        const action = String(payload?.Action || payload?.action || "").trim().toLowerCase();
        const watchlistStatusPayload = getWatchlistStatusUpdatePayload(payload);
        console.log(`${MA_MD_DEBUG_PREFIX} MA_INVENTORY_PAGE_MESSAGE action=${action || "<empty>"}`, payload);

        if (watchlistStatusPayload) {
          window.dispatchEvent(
            new CustomEvent("maWatchlistStatusUpdate", {
              detail: watchlistStatusPayload,
            })
          );
        }
        const hasPipelineHealth = Array.isArray(payload?.pipelines) && payload.pipelines.length > 0;
        console.log(`${MA_MD_DEBUG_PREFIX} MA_INVENTORY_PAGE_MESSAGE action=${action || "<empty>"}`, payload);

        if (hasPipelineHealth) {
          window.dispatchEvent(
            new CustomEvent("maInventoryPageHealthUpdate", {
              detail: { payload },
            })
          );
        }

        if (action === "clear") {
          window.dispatchEvent(
            new CustomEvent("maInventoryPageClear", {
              detail: { action: "clear", payload },
            })
          );
        }
      });

      if (maInventoryPageSub) {
        subscriptionsRef.current.push(maInventoryPageSub);
      }
    } else if (groupName === "secMaster") {
      const secMasterUpdatePath = "/topic/sec-master-security-update";
      const secMasterSub = safeSubscribe(client, secMasterUpdatePath, groupName, (message) => {
        if (!mountedRef.current) return;
        let payload = null;
        try {
          payload = JSON.parse(message?.body);
        } catch (error) {
          console.warn("[SecMaster WS] Failed to parse message", error, message?.body);
          return;
        }
        console.log("[SecMaster WS] sec-master-security-update received", payload);
        window.dispatchEvent(new CustomEvent("secMasterSecurityUpdate", { detail: payload }));
      });
      if (secMasterSub) subscriptionsRef.current.push(secMasterSub);

      const filterRefreshPath = "/topic/sec-master-filter-refresh";
      const filterRefreshSub = safeSubscribe(client, filterRefreshPath, groupName, (message) => {
        if (!mountedRef.current) return;
        let payload = null;
        try {
          payload = JSON.parse(message?.body);
        } catch (error) {
          console.warn("[SecMaster WS] Failed to parse filter-refresh message", error, message?.body);
          return;
        }
        const action = String(payload?.action || "").trim().toLowerCase();
        if (action !== "refresh") return;
        console.log("[SecMaster WS] sec-master-filter-refresh received");
        window.dispatchEvent(new CustomEvent("secMasterFilterRefresh"));
      });
      if (filterRefreshSub) subscriptionsRef.current.push(filterRefreshSub);
    } else {
      console.warn(`[${instanceId}] subscribeGroup: unknown group=${groupName}`);
      return; // Don't mark unknown groups as done
    }

    subscribedGroupsRef.current.add(groupName);
    console.log(`[${instanceId}] subscribeGroup: done group=${groupName}`);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleDbSubscriptionAck, userId, maInventoryRequestTraceId, currentAccountId]);

  // ---------------------------------------------------------------------------
  // subscribeAccountSpecific — subscribe account-scoped paths (accountSummary + position-update)
  // Called from subscribeGroup('accountPositions') and from the accountId-change effect.
  // ---------------------------------------------------------------------------
  const subscribeAccountSpecific = (client, accountId) => {
    if (!client || !client.connected || !accountId) return;

    // Account summary
    const accountSummaryPath = `/user/${accountId}/account-mapping/accountSummary`;
    if (!client._accountSummarySub) client._accountSummarySub = {};
    if (client._accountSummarySub[accountId]) {
      client._accountSummarySub[accountId].unsubscribe();
      console.log(`[WebSocket account specific] Unsubscribed from previous account summary for accountId: ${accountId}`);
    }
    console.log(`[WebSocket account specific] Subscribing to account summary for accountId: ${accountId} at ${accountSummaryPath}`);
    client._accountSummarySub[accountId] = client.subscribe(accountSummaryPath, (message) => {
      if (!mountedRef.current) return;
      const summary = JSON.parse(message.body);
      console.log(`[WebSocket account specific] Received account summary for accountId ${accountId}:`, summary);
      window.dispatchEvent(new CustomEvent("accountSummary", { detail: summary }));
    });

    // Position update
    const positionUpdatePath = `/user/${accountId}/account-mapping/position-update`;
    if (!client._positionUpdateSub) client._positionUpdateSub = {};
    if (client._positionUpdateSub[accountId]) {
      client._positionUpdateSub[accountId].unsubscribe();
      console.log(`[WebSocket account specific] Unsubscribed from previous position update for accountId: ${accountId}`);
    }
    console.log(`[WebSocket account specific] Subscribing to position update for accountId: ${accountId} at ${positionUpdatePath}`);
    client._positionUpdateSub[accountId] = client.subscribe(positionUpdatePath, (message) => {
      if (!mountedRef.current) return;
      const positionUpdate = JSON.parse(message.body);
      console.log(`[WebSocket account specific] Received position update for accountId ${accountId}:`, positionUpdate);
      window.dispatchEvent(new CustomEvent("positionUpdate", { detail: positionUpdate }));
    });
  };

  const unsubscribeAccountSpecific = () => {
    if (accountUpdateSubscriptionRef.current) {
      accountUpdateSubscriptionRef.current.unsubscribe();
      accountUpdateSubscriptionRef.current = null;
      console.log(`[WebSocket] Unsubscribed from account update`);
    }
    if (stompClientRef.current?._accountSummarySub) {
      Object.values(stompClientRef.current._accountSummarySub).forEach(sub => sub.unsubscribe());
      stompClientRef.current._accountSummarySub = {};
      console.log(`[WebSocket] Unsubscribed from all account summary subscriptions`);
    }
    if (stompClientRef.current?._positionUpdateSub) {
      Object.values(stompClientRef.current._positionUpdateSub).forEach(sub => sub.unsubscribe());
      stompClientRef.current._positionUpdateSub = {};
      console.log(`[WebSocket] Unsubscribed from all position update subscriptions`);
    }
  };

  // Make disconnect accept a "force" flag and handle both active/connected
  const disconnect = async (force = false) => {
    if (!force && connectionState === "disconnected") return;
    try {
      console.log(`[${instanceId}] Disconnecting WebSocket... (force=${force})`);
      updateConnectionState("disconnecting");
      clearMAInventorySubscriptions("disconnect");
      subscriptionsRef.current.forEach((sub) => sub.unsubscribe());
      subscriptionsRef.current = [];
      activeSubscriptionsPathsRef.current.clear();
      subscribedGroupsRef.current.clear();

      if (stompClientRef.current?.active || stompClientRef.current?.connected) {
        await stompClientRef.current.deactivate();
      }
      stompClientRef.current = null;
      updateConnectionState("disconnected");
    } catch (error) {
      console.error("Error during disconnect:", error);
      updateConnectionState("disconnected");
    }
  };

  // New: hard reconnect utility
  const hardReconnect = async (reason = "") => {
    console.warn(`[${instanceId}] Hard reconnect requested. Reason: ${reason}`);
    await disconnect(true);
    await connect();
  };

  const connect = async () => {
    if (!isLoggedIn) {
      console.log(`[${instanceId}] Connect blocked: User is not logged in.`);
      return;
    }
    if (!jwtToken) {
      console.log(`[${instanceId}] Connect blocked: JWT token is missing.`);
      return;
    }
    if (!mountedRef.current) {
      console.log(`[${instanceId}] Connect blocked: Component is not mounted.`);
      return;
    }
    if (connectInProgressRef.current) {
      console.log(`[${instanceId}] Connect blocked: Another connect is in progress.`);
      return;
    }
    if (stompClientRef.current?.active && stompClientRef.current?.connected) {
      console.log(`[${instanceId}] Connect skipped: Already connected and active.`);
      return;
    }

    try {
      connectInProgressRef.current = true;
      lastConnectAttemptRef.current = Date.now();
      console.log(`[${instanceId}] Connecting WebSocket for user ${userId}...`);
      subscriptionsRef.current.forEach((sub) => sub.unsubscribe());
      subscriptionsRef.current = [];
      activeSubscriptionsPathsRef.current.clear();
      subscribedGroupsRef.current.clear();
      updateConnectionState("connecting");
      const decodedToken = jwtDecode(jwtToken);
      decodedTokenRef.current = decodedToken;

      // If there is an old client still active/connected, tear it down first
      if (stompClientRef.current?.active || stompClientRef.current?.connected) {
        await disconnect(true);
      }

      const socket = new SockJS(
        `${process.env.REACT_APP_API_URL}/ws?token=${jwtToken}`,
        null,
        {
          timeout: 15000,
          transports: ["websocket", "xhr-streaming", "xhr-polling"],
        }
      );

      const client = new Client({
        webSocketFactory: () => socket,
        reconnectDelay: 5000,
        heartbeatIncoming: 4000,
        heartbeatOutgoing: 4000,
        onConnect: () => {
          if (!mountedRef.current) return;
          updateConnectionState("connected");
          lastConnectedAtRef.current = Date.now();
          console.log(`[${instanceId}] WebSocket connected for user ${userId}`);
          logMAInventoryProbe("CONNECTED", {
            userId,
            desiredCusipCount: desiredMAInventoryCusips.length,
            traceId: maInventoryRequestTraceId || "<empty>",
          });
          subscriptionsRef.current.forEach((sub) => sub.unsubscribe());
          subscriptionsRef.current = [];
          activeSubscriptionsPathsRef.current.clear();
          subscribedGroupsRef.current.clear();

          // Always subscribe global group on connect
          subscribeGroup(client, "global");

          // Subscribe any groups that were already visited before this connect
          pageSubscriptionGroups.forEach((group) => {
            subscribeGroup(client, group);
          });

          // Restore account-specific subscriptions if an account is already selected
          const selectedAccount =
            window.selectedAccountId || localStorage.getItem("selectedAccountId");
          if (currentAccountId) {
            // Effect will handle it when connectionState becomes 'connected'
          } else if (selectedAccount) {
            setCurrentAccountId(selectedAccount);
          }
        },
        onDisconnect: () => {
          if (!mountedRef.current) return;
          console.log(`[${instanceId}] WebSocket disconnected`);
          updateConnectionState("disconnected");
          subscriptionsRef.current = [];
          activeSubscriptionsPathsRef.current.clear();
          subscribedGroupsRef.current.clear();
        },
        onStompError: (frame) => {
          if (!mountedRef.current) return;
          console.error("STOMP error:", frame);
          logMAInventoryProbe("STOMP_ERROR", frame);
          updateConnectionState("disconnected");
        },
        onWebSocketClose: (evt) => {
          if (!mountedRef.current) return;
          console.warn(`[${instanceId}] WebSocket closed`, evt?.reason || "");
          logMAInventoryProbe("WS_CLOSE", evt);
          updateConnectionState("disconnected");
        },
        onWebSocketError: (evt) => {
          if (!mountedRef.current) return;
          console.error(`[${instanceId}] WebSocket error`, evt?.message || evt);
          logMAInventoryProbe("WS_ERROR", evt);
          updateConnectionState("disconnected");
        }
      });

      stompClientRef.current = client;
      await client.activate();
    } catch (error) {
      console.error(`[${instanceId}] Connection error:`, error);
      updateConnectionState("disconnected");
    } finally {
      connectInProgressRef.current = false;
    }
  };

  // ---------------------------------------------------------------------------
  // Effect: when pageSubscriptionGroups changes (a new page is visited),
  // subscribe the new group if currently connected. Also unsubscribe removed groups.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (connectionState !== "connected" || !stompClientRef.current?.connected) return;

    pageSubscriptionGroups.forEach((group) => {
      if (!subscribedGroupsRef.current.has(group)) {
        subscribeGroup(stompClientRef.current, group);
      }
    });
    
    // global is always subscribed and not tracked in pageSubscriptionGroups
    const groupsToUnsubscribe = [];
    subscribedGroupsRef.current.forEach((group) => {
      if (group !== "global" && !pageSubscriptionGroups.has(group)) {
        groupsToUnsubscribe.push(group);
      }
    });

    groupsToUnsubscribe.forEach((group) => {
      unsubscribeGroup(group);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSubscriptionGroups, connectionState, subscribeGroup, unsubscribeGroup]);

  // Mount / unmount
  useEffect(() => {
    console.log(`[${instanceId}] Component mounted with user ${userId}, role: ${userRole}, logged in: ${isLoggedIn}`);
    if (isInstanceMounted) {
      console.error("WARNING: Multiple WebSocketComponent instances detected. This will cause duplicate messages!");
      console.error("Please ensure WebSocketComponent is only included ONCE in your application.");
    }
    isInstanceMounted = true;
    mountedRef.current = true;
    connect();

    window.websocketInstances = window.websocketInstances || [];
    window.websocketInstances.push(instanceId);
    console.log(`[WEBSOCKET] Total WebSocketComponent instances: ${window.websocketInstances.length}`);

    return () => {
      console.log(`[${instanceId}] Component unmounting`);
      mountedRef.current = false;
      connectInProgressRef.current = false;
      disconnect(true);
      disconnect();
      isInstanceMounted = false;
      window.websocketInstances = window.websocketInstances.filter(id => id !== instanceId);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, userRole, jwtToken, isLoggedIn]);

  // Watch for logout events
  useEffect(() => {
    if (!isLoggedIn && (connectionState === 'connected' || connectionState === 'connecting')) {
      console.log(`[${instanceId}] User logged out, disconnecting WebSocket immediately`);
      disconnect(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  // Connection health check
  const isConnectionHealthy = () => {
    const client = stompClientRef.current;
    const wsReady = client?.webSocket?.readyState === 1;
    return !!(client && client.active && client.connected && wsReady);
  };

  // Visibility change handler
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (!isConnectionHealthy()) {
          console.log(`[${instanceId}] Tab visible and connection unhealthy; performing hard reconnect.`);
          hardReconnect("visibilitychange_unhealthy");
        } else {
          console.log(`[${instanceId}] Tab visible and connection healthy.`);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionState]);

  // Watchdog timer
  useEffect(() => {
    if (!mountedRef.current) return;
    if (healthCheckTimerRef.current) {
      clearInterval(healthCheckTimerRef.current);
      healthCheckTimerRef.current = null;
    }

    healthCheckTimerRef.current = setInterval(async () => {
      if (!mountedRef.current || !isLoggedIn) return;

      if (isConnectionHealthy()) return;

      const now = Date.now();
      const sinceLastAttempt = now - lastConnectAttemptRef.current;
      const sinceLastConnected = lastConnectedAtRef.current ? now - lastConnectedAtRef.current : Infinity;

      if (!stompClientRef.current?.active) {
        console.log(`[${instanceId}] Watchdog: client inactive; connecting.`);
        await connect();
        return;
      }

      if (sinceLastConnected > 20000 || sinceLastAttempt > 15000) {
        console.warn(`[${instanceId}] Watchdog: stale connection detected (lastConnected=${sinceLastConnected}ms, lastAttempt=${sinceLastAttempt}ms). Hard reconnecting.`);
        await hardReconnect("watchdog_stale");
      }
    }, 7000);

    return () => {
      if (healthCheckTimerRef.current) {
        clearInterval(healthCheckTimerRef.current);
        healthCheckTimerRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  // ---------------------------------------------------------------------------
  // Account-specific subscriptions — re-run when accountId changes or on connect
  // (only if accountPositions group has been visited)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!stompClientRef.current || !stompClientRef.current.connected) return;
    if (!subscribedGroupsRef.current.has("accountPositions")) return;

    unsubscribeAccountSpecific();
    if (currentAccountId) {
      subscribeAccountSpecific(stompClientRef.current, currentAccountId);
    }

    return () => {
      unsubscribeAccountSpecific();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAccountId, connectionState]);

  // Listen for account selection changes
  useEffect(() => {
    const handleAccountSelection = (event) => {
      const accountId = event.detail;
      setCurrentAccountId(accountId);
    };
    window.addEventListener("activeAccountChanged", handleAccountSelection);
    return () => {
      window.removeEventListener("activeAccountChanged", handleAccountSelection);
    };
  }, []);

  // On mount, restore selectedAccountId from storage
  useEffect(() => {
    const selectedAccount = window.selectedAccountId || localStorage.getItem('selectedAccountId');
    if (selectedAccount) {
      setCurrentAccountId(selectedAccount);
    }
  }, []);

  return null;
};

export default WebSocketComponent;
